import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Receipt, Images, ChevronDown, Package, CheckCircle2 } from "lucide-react";
import { API, fmtDateUS } from "@/lib/api";
import { DateUSInput } from "@/components/inputs";

const inputCls = "w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";

const useCollapse = (key, def = false) => {
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(key); return v === null ? def : v === "1"; } catch { return def; }
  });
  const toggle = () => { const n = !open; setOpen(n); try { localStorage.setItem(key, n ? "1" : "0"); } catch {} };
  return [open, toggle];
};

const TABS = [
  ["progress", "Progress"],
  ["receipts", "Receipts"],
  ["report", "Report"],
  ["messages", "Messages"],
  ["materials", "Materials"],
];

export const ForemanProjectCard = ({ job, emp, empFetch, onChanged }) => {
  const [open, toggleOpen] = useCollapse(`time-proj-open-${job.id}`, false);
  const [tab, setTab] = useState("progress");
  const [itemPcts, setItemPcts] = useState({});
  const [pctBusy, setPctBusy] = useState(false);
  const [qText, setQText] = useState("");
  const [qBusy, setQBusy] = useState(false);
  const [exp, setExp] = useState({ amount: "", note: "", billable: false, itemIds: [] });
  const [expFile, setExpFile] = useState(null);
  const [expBusy, setExpBusy] = useState(false);
  const [expDone, setExpDone] = useState(null); // "receipt submitted" confirmation — stops double submits
  const [rep, setRep] = useState({ report_date: today(), progress: "", workers_count: "", purchases: "", concerns: "", delayed: false, delay_reason: "", material_missing: false, material_note: "" });
  const [repPhotos, setRepPhotos] = useState([]);
  const [repBusy, setRepBusy] = useState(false);
  const [mat, setMat] = useState({ item: "", quantity: "", note: "" });
  const [matBusy, setMatBusy] = useState(false);

  function today() { return new Date().toISOString().slice(0, 10); }

  const uploadFiles = async (path, fd) => {
    const token = localStorage.getItem("is_emp_token");
    const res = await fetch(`${API}/api${path}`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
    return data;
  };

  const changedPcts = () => (job.progress?.items || []).filter(
    (it) => itemPcts[it.id] !== undefined && itemPcts[it.id] !== String(it.pct));

  const saveAllPcts = async () => {
    const changed = changedPcts();
    if (!changed.length) return toast.error("No percentages changed");
    for (const it of changed) {
      const pct = parseFloat(itemPcts[it.id]);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) { toast.error(`${it.name}: enter a percent between 0 and 100`); return; }
    }
    setPctBusy(true);
    try {
      for (const it of changed) {
        await empFetch(`/time/jobs/${job.id}/items/${it.id}/progress`, { method: "POST", body: JSON.stringify({ pct: parseFloat(itemPcts[it.id]) }) });
      }
      toast.success(`Saved ${changed.length} item${changed.length === 1 ? "" : "s"}`);
      setItemPcts({});
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPctBusy(false);
    }
  };

  const sendQuestion = async () => {
    if (!qText.trim()) return toast.error("Type the question");
    setQBusy(true);
    try {
      await empFetch(`/time/jobs/${job.id}/questions`, { method: "POST", body: JSON.stringify({ question: qText }) });
      toast.success("Question sent to the client");
      setQText("");
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setQBusy(false);
    }
  };

  const submitExpense = async () => {
    if (!exp.amount || parseFloat(exp.amount) <= 0) { toast.error("Enter the amount first"); return; }
    if (!expFile) { toast.error("Snap the receipt photo first"); return; }
    setExpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", expFile);
      fd.append("amount", exp.amount);
      fd.append("note", exp.note);
      fd.append("billable", exp.billable ? "true" : "false");
      fd.append("item_ids", exp.itemIds.join(","));
      const data = await uploadFiles(`/time/jobs/${job.id}/expenses`, fd);
      setExpDone({ amount: data.amount, at: new Date().toISOString() });
      setExp({ amount: "", note: "", billable: false, itemIds: [] });
      setExpFile(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExpBusy(false);
    }
  };

  const submitReport = async () => {
    if (!rep.progress.trim()) return toast.error("Write which areas you worked on");
    if (rep.delayed && !rep.delay_reason.trim()) return toast.error("What's causing the delay?");
    setRepBusy(true);
    try {
      const created = await empFetch(`/time/jobs/${job.id}/reports`, {
        method: "POST",
        body: JSON.stringify({ ...rep, workers_count: parseInt(rep.workers_count, 10) || 0 }),
      });
      if (repPhotos.length > 0) {
        const fd = new FormData();
        repPhotos.slice(0, 6).forEach((f) => fd.append("files", f));
        await uploadFiles(`/time/jobs/${job.id}/reports/${created.id}/photos`, fd);
      }
      toast.success("Daily report sent to the office");
      setRep({ report_date: today(), progress: "", workers_count: "", purchases: "", concerns: "", delayed: false, delay_reason: "", material_missing: false, material_note: "" });
      setRepPhotos([]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRepBusy(false);
    }
  };

  const submitMaterial = async () => {
    if (!mat.item.trim()) return toast.error("What material do you need?");
    setMatBusy(true);
    try {
      await empFetch(`/time/jobs/${job.id}/material-orders`, { method: "POST", body: JSON.stringify(mat) });
      toast.success(job.supply_mode === "labor" ? "Request sent to the client" : "Request sent to the office");
      setMat({ item: "", quantity: "", note: "" });
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMatBusy(false);
    }
  };

  const overallPct = job.progress?.overall_pct ?? job.completion_pct ?? 0;

  return (
    <div className="border border-white/10 rounded-2xl bg-card overflow-hidden" data-testid={`foreman-project-${job.id}`}>
      <button onClick={toggleOpen} data-testid={`proj-toggle-${job.id}`} aria-expanded={open} className="w-full text-left p-5 space-y-2 hover:bg-white/[0.03] transition-colors duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-display text-2xl tracking-wide truncate">{job.title}</p>
            <p className="text-[11px] text-white/45">{job.client_name}</p>
            {job.address && <p className="text-[11px] text-white/45 flex items-center gap-1 mt-0.5"><MapPin size={11} className="text-primary shrink-0" aria-hidden="true" />{job.address}</p>}
          </div>
          <ChevronDown size={18} className={`shrink-0 text-white/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </div>
        <div className="flex items-center gap-2.5">
          <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${overallPct}%` }} />
          </div>
          <span className="text-[11px] font-bold text-primary shrink-0" data-testid={`proj-overall-${job.id}`}>{overallPct}%</span>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          <div className="flex gap-1 overflow-x-auto -mx-1 px-1" data-testid={`proj-tabs-${job.id}`}>
            {TABS.map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`proj-tab-${key}-${job.id}`}
                      className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest border rounded-lg whitespace-nowrap transition-colors duration-200 ${tab === key ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/50"}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === "progress" && (
            <div className="space-y-2" data-testid={`proj-progress-${job.id}`}>
              {(job.progress?.categories || []).map((cat) => (
                <div key={cat.name} className="border border-white/10 rounded-lg bg-background/60 px-3 py-2.5 space-y-2" data-testid={`proj-cat-${job.id}-${cat.name}`}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/55">{cat.name} — {cat.pct}%</p>
                  {(job.progress?.items || []).filter((i) => i.category === cat.name).map((it) => (
                    <div key={it.id} className="flex items-center gap-2" data-testid={`proj-item-${it.id}`}>
                      <span className="flex-1 min-w-0 text-xs text-white/75 truncate">{it.name}{it.source === "extra" ? " (extra)" : ""}</span>
                      <input
                        type="number" min="0" max="100" step="1"
                        value={itemPcts[it.id] ?? String(it.pct)}
                        onChange={(e) => setItemPcts({ ...itemPcts, [it.id]: e.target.value })}
                        aria-label={`${it.name} percent complete`}
                        data-testid={`proj-item-pct-${it.id}`}
                        className="w-16 shrink-0 bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-primary"
                      />
                      <span className="text-[10px] text-white/40 shrink-0">%</span>
                    </div>
                  ))}
                </div>
              ))}
              {(job.progress?.items || []).length > 0 && (
                <button onClick={saveAllPcts} disabled={pctBusy} data-testid={`proj-save-all-${job.id}`} className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs py-3.5 rounded-lg transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                  {pctBusy ? "Saving..." : `Submit all percentages${changedPcts().length ? ` (${changedPcts().length} changed)` : ""}`}
                </button>
              )}
              {(job.progress?.items || []).length === 0 && (
                <p className="text-[11px] text-white/35">No tracked items on this project yet.</p>
              )}
            </div>
          )}

          {tab === "receipts" && (
            <div className="space-y-2" data-testid={`proj-expense-${job.id}`}>
              {expDone && (
                <div className="border border-primary/50 bg-primary/10 rounded-lg px-3.5 py-3 flex items-center gap-2.5" data-testid={`proj-exp-done-${job.id}`}>
                  <CheckCircle2 size={16} className="text-primary shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-primary uppercase tracking-widest">Receipt submitted</p>
                    <p className="text-[11px] text-white/60">${expDone.amount} — it's with the office now. No need to send it twice.</p>
                  </div>
                  <button onClick={() => setExpDone(null)} data-testid={`proj-exp-new-${job.id}`} className="text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-primary shrink-0">New receipt</button>
                </div>
              )}
              {!expDone && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min="0" step="0.01" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} placeholder="Amount $" data-testid={`proj-exp-amount-${job.id}`} className={inputCls} />
                    <input value={exp.note} onChange={(e) => setExp({ ...exp, note: e.target.value })} placeholder="What was it?" data-testid={`proj-exp-note-${job.id}`} className={inputCls} />
                  </div>
                  {(job.progress?.items || []).length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5">Which areas is this for? (cost splits evenly across your picks)</p>
                      <div className="flex flex-wrap gap-1.5" data-testid={`proj-exp-areas-${job.id}`}>
                        {job.progress.items.map((it) => {
                          const on = exp.itemIds.includes(it.id);
                          return (
                            <button key={it.id} type="button" onClick={() => setExp({ ...exp, itemIds: on ? exp.itemIds.filter((x) => x !== it.id) : [...exp.itemIds, it.id] })} data-testid={`proj-exp-area-${it.id}`}
                                    className={`px-2.5 py-1.5 text-[10px] font-bold border rounded-lg transition-colors duration-200 ${on ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/55"}`}>
                              {it.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <label className="flex items-center gap-2.5 text-sm text-white/70 cursor-pointer">
                    <input type="checkbox" checked={exp.billable} onChange={(e) => setExp({ ...exp, billable: e.target.checked })} data-testid={`proj-exp-billable-${job.id}`} className="w-4 h-4 accent-primary" />
                    Charge this to the client — goes on their next invoice at cost
                  </label>
                  <label className={`flex items-center justify-center gap-2 border border-dashed rounded-lg py-3.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 cursor-pointer ${expFile ? "border-primary/60 text-primary" : "border-white/25 text-white/60 hover:border-primary hover:text-primary"}`} data-testid={`proj-exp-upload-${job.id}`}>
                    <Receipt size={15} aria-hidden="true" /> {expFile ? `Photo picked: ${expFile.name.slice(0, 24)}` : "Snap receipt photo"}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { setExpFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                  </label>
                  <button onClick={submitExpense} disabled={expBusy} data-testid={`proj-exp-save-${job.id}`} className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 rounded-lg disabled:opacity-50">
                    {expBusy ? "Saving..." : exp.billable ? "Save — shows on the next invoice" : "Save expense"}
                  </button>
                </>
              )}
            </div>
          )}

          {tab === "report" && (
            <div className="space-y-2.5" data-testid={`proj-report-${job.id}`}>
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Date (MM/DD/YYYY)</p>
                  <DateUSInput value={rep.report_date} onChange={(v) => setRep({ ...rep, report_date: v })} testid={`proj-rep-date-${job.id}`} />
                </div>
                <input type="number" min="0" max="50" value={rep.workers_count} onChange={(e) => setRep({ ...rep, workers_count: e.target.value })} placeholder="# workers on site" data-testid={`proj-rep-workers-${job.id}`} className={inputCls} />
              </div>
              <textarea value={rep.progress} onChange={(e) => setRep({ ...rep, progress: e.target.value })} rows={2} placeholder="Areas worked on today *" data-testid={`proj-rep-areas-${job.id}`} className={`${inputCls} resize-none`} />
              <input value={rep.purchases} onChange={(e) => setRep({ ...rep, purchases: e.target.value })} placeholder="Purchases / receipts today (optional)" data-testid={`proj-rep-purchases-${job.id}`} className={inputCls} />
              <textarea value={rep.concerns} onChange={(e) => setRep({ ...rep, concerns: e.target.value })} rows={2} placeholder="Concerns (optional)" data-testid={`proj-rep-concerns-${job.id}`} className={`${inputCls} resize-none`} />
              <label className="flex items-center gap-2.5 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={rep.delayed} onChange={(e) => setRep({ ...rep, delayed: e.target.checked })} data-testid={`proj-rep-delayed-${job.id}`} className="w-4 h-4 accent-primary" />
                Anything delaying progress?
              </label>
              {rep.delayed && <input value={rep.delay_reason} onChange={(e) => setRep({ ...rep, delay_reason: e.target.value })} placeholder="What's causing the delay?" data-testid={`proj-rep-delay-reason-${job.id}`} className={inputCls} />}
              <label className="flex items-center gap-2.5 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={rep.material_missing} onChange={(e) => setRep({ ...rep, material_missing: e.target.checked })} data-testid={`proj-rep-material-${job.id}`} className="w-4 h-4 accent-primary" />
                Any material missing?
              </label>
              {rep.material_missing && <input value={rep.material_note} onChange={(e) => setRep({ ...rep, material_note: e.target.value })} placeholder="What material is missing?" data-testid={`proj-rep-material-note-${job.id}`} className={inputCls} />}
              <label className="flex items-center justify-center gap-2 border border-dashed border-white/25 rounded-lg py-3.5 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 cursor-pointer" data-testid={`proj-rep-photos-${job.id}`}>
                <Images size={14} aria-hidden="true" /> {repPhotos.length > 0 ? `${repPhotos.length} photo(s) picked` : "Add photos (up to 6)"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { setRepPhotos(Array.from(e.target.files || []).slice(0, 6)); }} />
              </label>
              <button onClick={submitReport} disabled={repBusy} data-testid={`proj-rep-submit-${job.id}`} className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 rounded-lg disabled:opacity-50">
                {repBusy ? "Sending..." : "Submit Daily Report"}
              </button>
            </div>
          )}

          {tab === "messages" && (
            <div className="space-y-2" data-testid={`proj-questions-${job.id}`}>
              {(job.clarifications || []).length > 0 && (
                <div className="space-y-1.5">
                  {job.clarifications.slice(0, 5).map((c) => (
                    <div key={c.id} className="text-[11px] border border-white/10 rounded-lg px-3 py-2 bg-background/60" data-testid={`proj-q-${c.id}`}>
                      <p className="text-white/70">{c.question}</p>
                      {c.status === "closed"
                        ? <p className="text-primary/90 mt-1 border-l-2 border-primary pl-2">{c.answer} <span className="text-white/35">· {fmtDateUS(c.answered_at)}</span></p>
                        : <p className="text-amber-300/80 mt-1">Waiting for the client...</p>}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input value={qText} onChange={(e) => setQText(e.target.value)} placeholder="e.g. Confirm the shutter color?" data-testid={`proj-q-input-${job.id}`} className={inputCls} />
                <button onClick={sendQuestion} disabled={qBusy} data-testid={`proj-q-send-${job.id}`} className="px-5 border border-primary text-primary font-bold uppercase tracking-widest text-xs rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-300 disabled:opacity-50 shrink-0">
                  {qBusy ? "..." : "Send"}
                </button>
              </div>
            </div>
          )}

          {tab === "materials" && (
            <div className="space-y-2.5" data-testid={`proj-materials-${job.id}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 flex items-center gap-1.5"><Package size={11} className="text-primary" aria-hidden="true" /> Order material — {job.supply_mode === "labor" ? "the client buys materials on this job" : "the office orders materials on this job"}</p>
              {(job.material_orders || []).length > 0 && (
                <div className="space-y-1.5">
                  {job.material_orders.map((o) => (
                    <div key={o.id} className="text-[11px] border border-white/10 rounded-lg px-3 py-2 bg-background/60" data-testid={`proj-mat-${o.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-white/80 font-semibold">{o.item}{o.quantity ? ` · ${o.quantity}` : ""}</p>
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${o.status === "ordered" ? "bg-emerald-400/20 text-emerald-300" : "bg-amber-400/20 text-amber-300"}`}>{o.status}</span>
                      </div>
                      {o.eta && <p className="text-white/50 mt-0.5">ETA: {o.eta}{o.answered_by ? ` · by ${o.answered_by}` : ""}</p>}
                      {o.response_note && <p className="text-white/50 mt-0.5">{o.response_note}</p>}
                    </div>
                  ))}
                </div>
              )}
              <input value={mat.item} onChange={(e) => setMat({ ...mat, item: e.target.value })} placeholder="Material needed — e.g. Hardie trim boards" data-testid={`proj-mat-item-${job.id}`} className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input value={mat.quantity} onChange={(e) => setMat({ ...mat, quantity: e.target.value })} placeholder="Qty — e.g. 20 pcs" data-testid={`proj-mat-qty-${job.id}`} className={inputCls} />
                <input value={mat.note} onChange={(e) => setMat({ ...mat, note: e.target.value })} placeholder="Note (color, size…)" data-testid={`proj-mat-note-${job.id}`} className={inputCls} />
              </div>
              <button onClick={submitMaterial} disabled={matBusy} data-testid={`proj-mat-send-${job.id}`} className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 rounded-lg disabled:opacity-50">
                {matBusy ? "Sending..." : "Request Material"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
